import React, { useMemo } from 'react';
import clsx from 'clsx';
import { MessageDescriptor, useIntl } from 'react-intl';
import { CatalogItemKind } from 'fym-shared/src/smartlinks/catalog';
import { TypedMemo } from '../../../../common/types';
import { CatalogItem } from '../../../../../redux/api/fymAPI.types';
import { songPickerMessages as m } from './songPickerMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the picker; each part uses its own slice of it */
import styles from './SongPicker.module.scss';
/* eslint-enable css-modules/no-unused-class */

export type FactTone = 'plain' | 'ok' | 'warn' | 'live';

export interface SongFact {
  key: string;
  tone: FactTone;
  message: MessageDescriptor;
}

/**
 * The facts of a song as small pills (design t1): the master, the lyrics, and the release (the
 * pre-save before the release day, "Released" after it). Pure.
 *
 * The catalog row has no state of the pre-save campaign, so a row with a campaign shows
 * "Pre-save live".
 */
export function songFacts(item: CatalogItem, days: number | null): SongFact[] {
  const { song } = item;
  const facts: SongFact[] = [];
  if (!song.hasMaster || song.masterStatus === null) {
    facts.push({ key: 'master', tone: 'warn', message: m.factNoMaster });
  } else if (song.masterStatus === 'failed') {
    facts.push({ key: 'master', tone: 'warn', message: m.factMasterFailed });
  } else if (song.masterStatus === 'processing') {
    facts.push({ key: 'master', tone: 'plain', message: m.factMasterProcessing });
  } else {
    facts.push({ key: 'master', tone: 'ok', message: m.factMaster });
  }

  if (song.lyricSyncStatus === 'synced') {
    facts.push({ key: 'lyrics', tone: 'ok', message: m.factLyricsSynced });
  } else if (song.lyricSyncStatus === 'syncing') {
    facts.push({ key: 'lyrics', tone: 'plain', message: m.factLyricsSyncing });
  } else if (song.hasLyrics) {
    facts.push({ key: 'lyrics', tone: 'plain', message: m.factLyrics });
  } else {
    facts.push({ key: 'lyrics', tone: 'plain', message: m.factNoLyrics });
  }

  const isReleased = days === null ? item.kind !== CatalogItemKind.unreleased : days <= 0;
  if (isReleased) {
    facts.push({ key: 'release', tone: 'ok', message: m.factReleased });
  } else if (item.presaveCampaignUuid !== null) {
    facts.push({ key: 'release', tone: 'live', message: m.factPreSaveLive });
  } else {
    facts.push({ key: 'release', tone: 'plain', message: m.factNoPreSave });
  }
  return facts;
}

const TONE_CLASS: Readonly<Record<FactTone, string | undefined>> = {
  plain: undefined,
  ok: styles.factOk,
  warn: styles.factWarn,
  live: styles.factLive,
};

interface SongPickerFactsProps {
  item: CatalogItem;
  days: number | null;
}

const SongPickerFactsComponent: React.FC<SongPickerFactsProps> = ({ item, days }) => {
  const intl = useIntl();
  const facts = useMemo(() => songFacts(item, days), [days, item]);
  return (
    <div className={styles.facts}>
      {facts.map((fact) => (
        <span key={fact.key} className={clsx(styles.fact, TONE_CLASS[fact.tone])} data-tone={fact.tone}>
          {intl.formatMessage(fact.message)}
        </span>
      ))}
    </div>
  );
};

export const SongPickerFacts = TypedMemo(SongPickerFactsComponent);
